/**
 * 项目相关 API Hooks
 */
import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { projectApi } from '@/services/api';
import type { EpisodeDeleteOptions, EpisodeDeletePreview } from '@/services/api/projects';
import { folderKeys, invalidateFolderTrees } from '@/hooks/api/useFolders';
import { orgProjectBudgetTreeKeys } from '@/hooks/api/useOrgProjectBudgetTree';
import { invalidateAllProjectElements } from '@/hooks/api/useProjectElements';
import { invalidateAllElementDetails } from '@/hooks/api/useProjectElementsDetail';
import { invalidateAllProjectElementTags } from '@/hooks/api/useProjectElementTags';
import {
  invalidateResourceLibraryInfinites,
  invalidateResourceLibraryLists,
} from '@/hooks/api/useResourceLibrary';
import { invalidateAllStoryboardBoardQueries } from '@/hooks/api/useStoryboardBoard';
import { invalidateAllStoryboardQueries } from '@/hooks/api/useStoryboards';
import { invalidateMentions } from '@/hooks/api/useMentionableResources';
import { useOrgContextStore } from '@/stores/orgContextStore';
import { CONTEXTS_QUERY_KEY } from '@/lib/context-query-keys';
import { resyncNotificationModel } from '@/hooks/api/useNotifications';
import { clearStoryboardBoardCache } from '@/lib/storyboard/board-cache';
import { orgManageCreditsKeys } from '@/hooks/api/useOrgManageCredits';
import type { TaskEvent } from '@/hooks/events';
import type {
  Project,
  ArchiveProjectDto,
  PauseProjectDto,
  CreateProjectDto,
  ProjectExportCapabilitiesResponse,
  ProjectExportRequest,
  UpdateProjectDto,
  SetProjectCoverImageDto,
  MemberConfigInheritanceRequest,
  MoveProjectDto,
  TransferProjectToOrgDto,
  ProjectListParams,
  ProjectExportStatusResponse,
  ProjectExportWsEvent,
} from '@/types';

// Query Keys
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (activeOrgId: string | null, filters?: ProjectListParams) =>
    [...projectKeys.lists(), activeOrgId, filters] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (activeOrgId: string | null, id: string) =>
    [...projectKeys.details(), activeOrgId, id] as const,
  entryContext: (activeOrgId: string | null, id: string | null) =>
    [...projectKeys.all, 'entry-context', activeOrgId, id] as const,
  exports: () => [...projectKeys.all, 'export'] as const,
  exportStatus: (exportJobId: string) => [...projectKeys.exports(), 'status', exportJobId] as const,
  exportCapabilities: (activeOrgId: string | null, id: string) =>
    [...projectKeys.exports(), 'capabilities', activeOrgId, id] as const,
  trash: () => [...projectKeys.all, 'trash'] as const,
  trashList: (activeOrgId: string | null, params?: { page?: number; pageSize?: number }) =>
    [...projectKeys.trash(), activeOrgId, params] as const,
  parentDirectory: (activeOrgId: string | null, parentProjectId: string | null) =>
    [...projectKeys.all, 'parent-directory', activeOrgId, parentProjectId] as const,
  directorySubprojects: (
    activeOrgId: string | null,
    parentProjectId: string | null,
    folderKey: string,
    sort: string,
    page: number | string
  ) =>
    [
      ...projectKeys.all,
      'directory-subprojects',
      activeOrgId,
      parentProjectId,
      folderKey,
      sort,
      page,
    ] as const,
  subprojectsRoot: (parentProjectId: string | null) =>
    [...projectKeys.all, 'subprojects', parentProjectId] as const,
  subprojects: (
    activeOrgId: string | null,
    parentProjectId: string | null,
    params?: {
      page?: number;
      pageSize?: number;
      includeArchived?: boolean;
    }
  ) => [...projectKeys.subprojectsRoot(parentProjectId), activeOrgId, params] as const,
  memberTargets: (parentProjectId: string | null) =>
    [...projectKeys.all, 'subproject-member-targets', parentProjectId] as const,
  memberManagement: (activeOrgId: string | null, parentProjectId: string | null) =>
    [...projectKeys.all, 'subproject-member-management', activeOrgId, parentProjectId] as const,
  episodeDeletePreview: (projectId: string | null) =>
    [...projectKeys.all, 'episode-delete-preview', projectId] as const,
  screenplayCreateGrants: (parentProjectId: string | null) =>
    [...projectKeys.all, 'screenplay-create-grants', parentProjectId] as const,
};

export function isTerminalProjectExportStatus(status?: string | null): boolean {
  if (!status) return false;
  return ['completed', 'failed', 'expired', 'cancelled', 'canceled'].includes(status.toLowerCase());
}

function normalizeProjectExportStatus(status?: string | null): string | null {
  if (!status) return null;
  const normalized = status.toLowerCase();
  return normalized === 'canceled' ? 'cancelled' : normalized;
}

function getProjectExportStatusPriority(status?: string | null): number {
  switch (normalizeProjectExportStatus(status)) {
    case 'failed':
    case 'expired':
    case 'cancelled':
      return 3;
    case 'completed':
      return 2;
    case 'processing':
      return 1;
    case 'pending':
      return 0;
    default:
      return -1;
  }
}

function chooseProjectExportStatus(
  nextStatus?: string | null,
  fallbackStatus?: string | null
): string {
  const normalizedNext = normalizeProjectExportStatus(nextStatus);
  const normalizedFallback = normalizeProjectExportStatus(fallbackStatus);
  if (!normalizedNext) return normalizedFallback ?? 'pending';
  if (!normalizedFallback) return normalizedNext;
  return getProjectExportStatusPriority(normalizedFallback) >
    getProjectExportStatusPriority(normalizedNext)
    ? normalizedFallback
    : normalizedNext;
}

function readStringField(
  event: TaskEvent,
  key: 'jobId' | 'fileName' | 'expiresAt' | 'errorMessage'
): string | null {
  const direct = event[key];
  if (typeof direct === 'string') return direct;
  const metadata = event.metadata?.[key];
  if (typeof metadata === 'string') return metadata;
  const result = event.result?.[key];
  return typeof result === 'string' ? result : null;
}

function readProjectExportNumberField(
  event: TaskEvent,
  key: 'fileSize' | 'creditsUsed' | 'sourceSizeBytes'
): number | null {
  const direct = event[key];
  if (typeof direct === 'number') return direct;
  const metadata = event.metadata?.[key];
  if (typeof metadata === 'number') return metadata;
  const result = event.result?.[key];
  return typeof result === 'number' ? result : null;
}

function readBooleanField(event: TaskEvent, key: 'canDownload'): boolean | null {
  const direct = event[key];
  if (typeof direct === 'boolean') return direct;
  const metadata = event.metadata?.[key];
  if (typeof metadata === 'boolean') return metadata;
  const result = event.result?.[key];
  return typeof result === 'boolean' ? result : null;
}

function clampProjectExportProgress(progress?: number | null): number {
  if (typeof progress !== 'number' || Number.isNaN(progress)) return 0;
  return Math.max(0, Math.min(100, progress));
}

function mergeProjectExportProgress(
  status: string,
  currentProgress?: number | null,
  previousProgress?: number | null
): number {
  if (status === 'completed') return 100;
  return Math.max(
    clampProjectExportProgress(currentProgress ?? 0),
    clampProjectExportProgress(previousProgress ?? 0)
  );
}

export function normalizeProjectExportTaskEvent(event: TaskEvent): ProjectExportWsEvent | null {
  const jobId = readStringField(event, 'jobId') ?? event.taskId ?? null;
  if (!jobId) return null;

  return {
    type: event.type,
    jobId,
    status: event.status ?? null,
    stage: event.stage ?? null,
    progress: event.progress ?? null,
    message: event.message ?? null,
    canDownload: readBooleanField(event, 'canDownload'),
    fileName: readStringField(event, 'fileName'),
    fileSize: readProjectExportNumberField(event, 'fileSize'),
    expiresAt: readStringField(event, 'expiresAt'),
    errorMessage: readStringField(event, 'errorMessage') ?? event.error?.message ?? null,
    creditsUsed: readProjectExportNumberField(event, 'creditsUsed'),
    sourceSizeBytes: readProjectExportNumberField(event, 'sourceSizeBytes'),
  };
}

export function inferProjectExportStatusFromEvent(
  event: ProjectExportWsEvent | null,
  fallbackStatus?: string | null
): string {
  if (!event) return fallbackStatus ?? 'pending';
  if (event.status) return chooseProjectExportStatus(event.status, fallbackStatus);

  const normalizedType = event.type.toLowerCase();
  if (
    normalizedType.includes('failed') ||
    normalizedType.includes('error') ||
    !!event.errorMessage
  ) {
    return chooseProjectExportStatus('failed', fallbackStatus);
  }
  if (normalizedType.includes('expired')) {
    return chooseProjectExportStatus('expired', fallbackStatus);
  }
  if (normalizedType.includes('cancelled') || normalizedType.includes('canceled')) {
    return chooseProjectExportStatus('cancelled', fallbackStatus);
  }
  if (
    normalizedType.includes('completed') ||
    normalizedType.includes('success') ||
    normalizedType.includes('done') ||
    event.canDownload
  ) {
    return chooseProjectExportStatus('completed', fallbackStatus);
  }
  return fallbackStatus ?? 'processing';
}

export function applyProjectExportTaskEventToCache(
  queryClient: QueryClient,
  exportJobId: string,
  event: TaskEvent
): ProjectExportWsEvent | null {
  const parsedEvent = normalizeProjectExportTaskEvent(event);
  if (!parsedEvent || parsedEvent.jobId !== exportJobId) return null;

  queryClient.setQueryData<ProjectExportStatusResponse>(
    projectKeys.exportStatus(exportJobId),
    (previous) => {
      const status = inferProjectExportStatusFromEvent(parsedEvent, previous?.status);
      return {
        exportJobId,
        status,
        stage: parsedEvent.stage ?? previous?.stage ?? null,
        progress: mergeProjectExportProgress(status, parsedEvent.progress, previous?.progress),
        canDownload:
          typeof parsedEvent.canDownload === 'boolean'
            ? parsedEvent.canDownload
            : (previous?.canDownload ?? false),
        fileName: parsedEvent.fileName ?? previous?.fileName ?? null,
        fileSize: parsedEvent.fileSize ?? previous?.fileSize ?? null,
        expiresAt: parsedEvent.expiresAt ?? previous?.expiresAt ?? null,
        errorMessage: parsedEvent.errorMessage ?? previous?.errorMessage ?? null,
        creditsUsed: parsedEvent.creditsUsed ?? previous?.creditsUsed ?? null,
        sourceSizeBytes: parsedEvent.sourceSizeBytes ?? previous?.sourceSizeBytes ?? null,
      };
    }
  );

  if (isTerminalProjectExportStatus(inferProjectExportStatusFromEvent(parsedEvent))) {
    void refetchProjectExportStatus(queryClient, exportJobId);
  }

  return parsedEvent;
}

export function refetchProjectExportStatus(
  queryClient: QueryClient,
  exportJobId: string
): Promise<void> {
  return queryClient
    .invalidateQueries({ queryKey: projectKeys.exportStatus(exportJobId) })
    .then(() => undefined);
}

export function useProjectExportTaskEventCache(exportJobId: string | null) {
  const queryClient = useQueryClient();

  const applyEvent = useCallback(
    (event: TaskEvent) => {
      if (!exportJobId) return null;
      return applyProjectExportTaskEventToCache(queryClient, exportJobId, event);
    },
    [exportJobId, queryClient]
  );

  const refetchStatus = useCallback(() => {
    if (!exportJobId) return Promise.resolve();
    return refetchProjectExportStatus(queryClient, exportJobId);
  }, [exportJobId, queryClient]);

  return { applyEvent, refetchStatus };
}

/**
 * 获取项目列表
 */
export function useProjects(
  params?: ProjectListParams,
  options?: { urlOrgId?: string | undefined }
) {
  const storeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const urlOrgId = options?.urlOrgId;
  const requestOrgId = urlOrgId !== undefined && urlOrgId !== '' ? urlOrgId : storeOrgId;

  return useQuery({
    queryKey: projectKeys.list(requestOrgId, params),
    queryFn: () =>
      projectApi.getAll(
        params,
        urlOrgId !== undefined && urlOrgId !== '' ? { activeOrgId: urlOrgId } : undefined
      ),
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
    refetchOnWindowFocus: params?.scope === 'collab' || params?.viewScope === 'external',
    refetchInterval:
      params?.scope === 'collab' || params?.viewScope === 'external' ? 1000 * 60 * 5 : false,
  });
}

/**
 * 获取单个项目
 */
export function useProject(
  id: string | null,
  options?: {
    enabled?: boolean;
    refetchOnMount?: boolean | 'always';
    refetchOnWindowFocus?: boolean;
    refetchInterval?: number | false;
    useListInitialData?: boolean;
    bypassDetailCache?: boolean;
  }
) {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const detailQueryKey = options?.bypassDetailCache
    ? ([...projectKeys.detail(activeOrgId, id!), 'verified'] as const)
    : projectKeys.detail(activeOrgId, id!);

  return useQuery({
    queryKey: detailQueryKey,
    queryFn: () => projectApi.getById(id!),
    enabled: (options?.enabled ?? true) && !!id,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
    refetchInterval: options?.refetchInterval ?? 1000 * 60 * 5,
    refetchOnMount: options?.refetchOnMount,
    initialData: () => {
      if (options?.useListInitialData === false) return undefined;
      // 尝试从列表缓存中查找
      const lists = queryClient.getQueriesData<{ items: Project[] }>({
        queryKey: projectKeys.lists(),
      });
      for (const [, data] of lists) {
        const project = data?.items?.find((p) => p.id === id);
        if (project) return project;
      }
      return undefined;
    },
  });
}

export function useProjectEntryContext(id: string | null, options?: { enabled?: boolean }) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useQuery({
    queryKey: projectKeys.entryContext(activeOrgId, id),
    queryFn: () => projectApi.getEntryContext(id!),
    enabled: (options?.enabled ?? true) && Boolean(id),
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

export function useCreateEmptyEpisode() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({
      parentProjectId,
      data,
    }: {
      parentProjectId: string;
      data?: Parameters<typeof projectApi.createEmptyEpisode>[1];
    }) => projectApi.createEmptyEpisode(parentProjectId, data, { activeOrgId }),
    onSuccess: (_episode, { parentProjectId }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.subprojectsRoot(parentProjectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

export function useParentDirectory(parentProjectId: string | null, enabled = true) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  return useQuery({
    queryKey: projectKeys.parentDirectory(activeOrgId, parentProjectId),
    queryFn: () => projectApi.getParentDirectory(parentProjectId!),
    enabled: enabled && Boolean(parentProjectId),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

export function useSubprojects(
  parentProjectId: string | null,
  options?: {
    enabled?: boolean;
    page?: number;
    pageSize?: number;
    includeArchived?: boolean;
  }
) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const params = {
    page: options?.page ?? 1,
    pageSize: options?.pageSize ?? 100,
    ...(options?.includeArchived === undefined ? {} : { includeArchived: options.includeArchived }),
  };

  return useQuery({
    queryKey: projectKeys.subprojects(activeOrgId, parentProjectId, params),
    queryFn: () => projectApi.getSubprojects(parentProjectId!, params),
    enabled: (options?.enabled ?? true) && Boolean(parentProjectId),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

export function useSubprojectMemberTargets(parentProjectId: string | null, enabled = true) {
  return useQuery({
    queryKey: projectKeys.memberTargets(parentProjectId),
    queryFn: () => projectApi.getSubprojectMemberTargets(parentProjectId!),
    enabled: enabled && Boolean(parentProjectId),
  });
}

export function useParentProjectMemberManagement(
  parentProjectId: string | null,
  options?: { activeOrgId?: string | null; enabled?: boolean }
) {
  const storeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const activeOrgId = options?.activeOrgId ?? storeOrgId;

  return useQuery({
    queryKey: projectKeys.memberManagement(activeOrgId ?? null, parentProjectId),
    queryFn: () => projectApi.getSubprojectMemberManagement(parentProjectId!, activeOrgId!),
    enabled: (options?.enabled ?? true) && Boolean(parentProjectId) && Boolean(activeOrgId),
  });
}

export function fetchSubprojectMemberManagement(parentProjectId: string, activeOrgId: string) {
  return projectApi.getSubprojectMemberManagement(parentProjectId, activeOrgId);
}

export function useSetSubprojectAsMemberConfigDefaultSource() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: (projectId: string) =>
      projectApi.setSubprojectAsMemberConfigDefaultSource(projectId),
    onSuccess: async (_summary, projectId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: projectKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: projectKeys.details() }),
        queryClient.invalidateQueries({ queryKey: projectKeys.detail(activeOrgId, projectId) }),
        invalidateFolderTrees(queryClient),
        queryClient.invalidateQueries({ queryKey: ['org-operation-logs'] }),
      ]);
    },
  });
}

export function useUpdateMemberConfigInheritance() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({
      parentProjectId,
      data,
    }: {
      parentProjectId: string;
      data: MemberConfigInheritanceRequest;
    }) => projectApi.updateMemberConfigInheritance(parentProjectId, data),
    onSuccess: async (_summary, { parentProjectId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: projectKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: projectKeys.details() }),
        queryClient.invalidateQueries({
          queryKey: projectKeys.detail(activeOrgId, parentProjectId),
        }),
        invalidateFolderTrees(queryClient),
        queryClient.invalidateQueries({ queryKey: ['org-operation-logs'] }),
      ]);
    },
  });
}

/**
 * 创建项目
 */
export function useCreateProject() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: (data: CreateProjectDto) => projectApi.create(data, { activeOrgId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      // 同步刷新文件夹树，org 上下文新建项目后侧边栏立即更新
      queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
      queryClient.invalidateQueries({ queryKey: ['context-resource-summary'] });
      if (activeOrgId) {
        queryClient.invalidateQueries({ queryKey: ['org', activeOrgId, 'credits'] });
        queryClient.invalidateQueries({
          queryKey: [...orgProjectBudgetTreeKeys.all, activeOrgId],
          exact: false,
        });
      }
    },
  });
}

export function useTransferProjectToOrg() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: TransferProjectToOrgDto }) =>
      projectApi.transferToOrg(projectId, data),
    onSuccess: (result, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.details() });
      queryClient.removeQueries({ queryKey: projectKeys.detail(null, projectId) });
      invalidateFolderTrees(queryClient);
      queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
      queryClient.invalidateQueries({ queryKey: CONTEXTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['context-resource-summary'] });
      queryClient.invalidateQueries({ queryKey: ['org', result.orgId, 'credits'] });
      queryClient.invalidateQueries({
        queryKey: orgManageCreditsKeys.allOrgCredits(result.orgId),
      });
      queryClient.invalidateQueries({
        queryKey: [...orgProjectBudgetTreeKeys.all, result.orgId],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: orgManageCreditsKeys.reports(result.orgId) });
      queryClient.invalidateQueries({ queryKey: ['org-operation-logs'] });
    },
  });
}

/**
 * 更新项目
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectDto }) =>
      projectApi.update(id, data),
    onSuccess: (updatedProject, { id, data }) => {
      // 更新单个项目缓存；合并旧权限字段，避免 PUT 响应缺 functionalRoles 导致 Parse Flow 误判只读
      queryClient.setQueryData<Project>(projectKeys.detail(activeOrgId, id), (previous) => {
        if (!previous) return updatedProject;
        const hasFunctionalRoles =
          Array.isArray(updatedProject.functionalRoles) &&
          updatedProject.functionalRoles.length > 0;
        return {
          ...updatedProject,
          memberRole: updatedProject.memberRole ?? previous.memberRole,
          functionalRoles: hasFunctionalRoles
            ? updatedProject.functionalRoles
            : previous.functionalRoles,
        };
      });
      // 失效项目列表查询
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      if (updatedProject.parentProjectId) {
        queryClient.invalidateQueries({
          queryKey: projectKeys.subprojectsRoot(updatedProject.parentProjectId),
        });
      }
      // 失效文件夹树查询，使工作区页面的项目列表（来自 useFolderTree）刷新
      queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
      queryClient.invalidateQueries({ queryKey: ['context-resource-summary'] });
      if (activeOrgId && data.aiCreditsBudget !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['org', activeOrgId, 'credits'] });
        queryClient.invalidateQueries({
          queryKey: [...orgProjectBudgetTreeKeys.all, activeOrgId],
          exact: false,
        });
      }
    },
  });
}

/**
 * 设置项目封面
 */
export function useSetProjectCoverImage() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SetProjectCoverImageDto }) =>
      projectApi.setCoverImage(id, data),
    onSuccess: (updatedProject, { id }) => {
      queryClient.setQueryData(projectKeys.detail(activeOrgId, id), updatedProject);
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      invalidateFolderTrees(queryClient);
    },
  });
}

/**
 * 删除项目（移至回收站）
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: (id: string) => projectApi.delete(id),
    onSuccess: () => {
      // 失效项目列表、回收站列表、文件夹树（工作区依赖树数据，删除后需刷新树以排除回收站项目）
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.trash() });
      invalidateFolderTrees(queryClient);
      if (activeOrgId) {
        queryClient.invalidateQueries({ queryKey: ['org', activeOrgId, 'credits'] });
        queryClient.invalidateQueries({
          queryKey: [...orgProjectBudgetTreeKeys.all, activeOrgId],
          exact: false,
        });
      }
    },
  });
}

/**
 * 删除项目下的单集（不进入回收站）
 */
function resolveEpisodeDeleteOptions(
  variables: string | { id: string; options?: EpisodeDeleteOptions }
): EpisodeDeleteOptions | undefined {
  return typeof variables === 'string' ? undefined : variables.options;
}

function invalidateEpisodeCreatedResourceCaches(queryClient: QueryClient): void {
  clearStoryboardBoardCache();
  invalidateResourceLibraryLists(queryClient);
  invalidateResourceLibraryInfinites(queryClient);
  invalidateAllProjectElements(queryClient);
  invalidateAllElementDetails(queryClient);
  invalidateAllProjectElementTags(queryClient);
  invalidateMentions(queryClient);
  queryClient.invalidateQueries({ queryKey: ['project-elements-for-picker'] });
  queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
  invalidateAllStoryboardQueries(queryClient);
  invalidateAllStoryboardBoardQueries(queryClient);
}

export function useEpisodeDeletePreview(projectId: string | null, options?: { enabled?: boolean }) {
  return useQuery<EpisodeDeletePreview>({
    queryKey: projectKeys.episodeDeletePreview(projectId),
    queryFn: () => projectApi.getEpisodeDeletePreview(projectId!),
    enabled: Boolean(projectId) && (options?.enabled ?? true),
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useDeleteEpisodeProject() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: (variables: string | { id: string; options?: EpisodeDeleteOptions }) =>
      typeof variables === 'string'
        ? projectApi.deleteEpisode(variables)
        : projectApi.deleteEpisode(variables.id, variables.options),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: ['screenplays'] });
      invalidateFolderTrees(queryClient);
      const options = resolveEpisodeDeleteOptions(variables);
      if (options?.deleteCreatedAssets || options?.deleteCreatedMaterials) {
        invalidateEpisodeCreatedResourceCaches(queryClient);
      }
      if (activeOrgId) {
        queryClient.invalidateQueries({ queryKey: ['org', activeOrgId, 'credits'] });
        queryClient.invalidateQueries({
          queryKey: [...orgProjectBudgetTreeKeys.all, activeOrgId],
          exact: false,
        });
      }
    },
  });
}

async function invalidateProjectArchiveCaches(
  queryClient: QueryClient,
  activeOrgId: string | null,
  projectId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: projectKeys.lists() }),
    // 根项目操作会联动单集，普通/verified 详情及不同身份缓存都按真实父子关系失效。
    queryClient.invalidateQueries({
      queryKey: projectKeys.details(),
      predicate: (query) =>
        query.queryKey[3] === projectId ||
        (query.state.data as Project | undefined)?.parentProjectId === projectId,
    }),
    queryClient.invalidateQueries({ queryKey: projectKeys.subprojectsRoot(projectId) }),
    queryClient.invalidateQueries({ queryKey: [...projectKeys.all, 'entry-context'] }),
    queryClient.invalidateQueries({ queryKey: folderKeys.trees() }),
    queryClient.invalidateQueries({ queryKey: CONTEXTS_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: ['context-resource-summary', activeOrgId] }),
    ...(activeOrgId
      ? [
          queryClient.invalidateQueries({ queryKey: ['org', activeOrgId, 'credits'] }),
          queryClient.invalidateQueries({
            queryKey: [...orgProjectBudgetTreeKeys.all, activeOrgId],
            exact: false,
          }),
        ]
      : []),
  ]);
}

export function useArchiveProject(activeOrgIdOverride?: string | null) {
  const queryClient = useQueryClient();
  const storedOrgId = useOrgContextStore((s) => s.activeOrgId);
  const activeOrgId = activeOrgIdOverride === undefined ? storedOrgId : activeOrgIdOverride;

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: ArchiveProjectDto }) =>
      projectApi.archiveProject(id, data, { activeOrgId }),
    onSuccess: async (_result, { id }) => {
      await Promise.all([
        invalidateProjectArchiveCaches(queryClient, activeOrgId, id),
        resyncNotificationModel(),
      ]);
    },
  });
}

export function useUnarchiveProject(activeOrgIdOverride?: string | null) {
  const queryClient = useQueryClient();
  const storedOrgId = useOrgContextStore((s) => s.activeOrgId);
  const activeOrgId = activeOrgIdOverride === undefined ? storedOrgId : activeOrgIdOverride;

  return useMutation({
    mutationFn: (id: string) => projectApi.unarchiveProject(id, { activeOrgId }),
    onSuccess: (_result, id) => invalidateProjectArchiveCaches(queryClient, activeOrgId, id),
  });
}

// 暂停/恢复保留原刷新范围，不复用归档/解档的父子项目及身份刷新。
function invalidateProjectPauseCaches(
  queryClient: QueryClient,
  activeOrgId: string | null,
  projectId: string
) {
  void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: projectKeys.detail(activeOrgId, projectId) });
  invalidateFolderTrees(queryClient);
  if (activeOrgId) {
    void queryClient.invalidateQueries({ queryKey: ['org', activeOrgId, 'credits'] });
    void queryClient.invalidateQueries({
      queryKey: [...orgProjectBudgetTreeKeys.all, activeOrgId],
      exact: false,
    });
  }
}

export function usePauseProject() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: PauseProjectDto }) =>
      projectApi.pauseProject(id, data, { activeOrgId }),
    onSuccess: (_result, { id }) => {
      invalidateProjectPauseCaches(queryClient, activeOrgId, id);
    },
  });
}

export function useResumeProject() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: (id: string) => projectApi.resumeProject(id, { activeOrgId }),
    onSuccess: (_result, id) => {
      invalidateProjectPauseCaches(queryClient, activeOrgId, id);
    },
  });
}

/**
 * 移动项目
 */
export function useMoveProject() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MoveProjectDto }) => projectApi.move(id, data),
    onSuccess: (movedProject, { id }) => {
      // 更新单个项目缓存
      queryClient.setQueryData(projectKeys.detail(activeOrgId, id), movedProject);
      // 失效列表查询
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/**
 * 克隆项目
 */
export function useCloneProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data?: { targetName?: string; targetFolderId?: string };
    }) => projectApi.clone(id, data),
    onSuccess: () => {
      // 克隆后工作区依赖的树缓存也需要刷新，否则需要手动刷新页面才能看到副本
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      invalidateFolderTrees(queryClient);
    },
  });
}

/**
 * 更新项目顺序
 */
export function useUpdateProjectOrder() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({ id, displayOrder }: { id: string; displayOrder: number }) =>
      projectApi.updateOrder(id, displayOrder),
    onSuccess: (updatedProject, { id }) => {
      // 更新单个项目缓存
      queryClient.setQueryData(projectKeys.detail(activeOrgId, id), updatedProject);
      // 失效列表查询
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      if (updatedProject.parentProjectId) {
        queryClient.invalidateQueries({
          queryKey: projectKeys.subprojectsRoot(updatedProject.parentProjectId),
        });
      }
    },
  });
}

/**
 * 创建项目导出任务
 */
export function useCreateProjectExportJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: ProjectExportRequest }) =>
      projectApi.createExportJob(projectId, data),
    onSuccess: (job) => {
      queryClient.setQueryData<ProjectExportStatusResponse>(
        projectKeys.exportStatus(job.exportJobId),
        {
          exportJobId: job.exportJobId,
          status: job.status,
          progress: job.progress,
          canDownload: false,
        }
      );
    },
  });
}

/**
 * 查询项目导出任务状态（支持轮询）
 */
export function useProjectExportJobStatus(
  exportJobId: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  return useQuery({
    queryKey: exportJobId ? projectKeys.exportStatus(exportJobId) : projectKeys.exports(),
    queryFn: () => projectApi.getExportJobStatus(exportJobId!),
    enabled: !!exportJobId && (options?.enabled ?? true),
    refetchInterval: (query) => {
      if (options?.refetchInterval === false) return false;
      const data = query.state.data;
      if (!data) return options?.refetchInterval ?? 2000;
      if (isTerminalProjectExportStatus(data.status)) {
        return false;
      }
      return options?.refetchInterval ?? 2000;
    },
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
  });
}

/**
 * 下载项目导出文件
 */
export function useDownloadProjectExportFile() {
  return useMutation({
    mutationFn: (exportJobId: string) => projectApi.getExportJobDownloadUrl(exportJobId),
  });
}

export function useProjectExportCapabilities(
  projectId: string | null,
  options?: { enabled?: boolean }
) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useQuery<ProjectExportCapabilitiesResponse>({
    queryKey: projectId
      ? projectKeys.exportCapabilities(activeOrgId, projectId)
      : projectKeys.exports(),
    queryFn: () => projectApi.getExportCapabilities(projectId!),
    enabled: Boolean(projectId) && (options?.enabled ?? true),
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 5,
  });
}

// ========================================================================
// 回收站相关 Hooks
// ========================================================================

/**
 * 获取回收站列表
 */
export function useTrashProjects(
  params?: { page?: number; pageSize?: number },
  options?: { enabled?: boolean }
) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useQuery({
    queryKey: projectKeys.trashList(activeOrgId, params),
    queryFn: () => projectApi.getTrash(params),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 2, // 2分钟
    gcTime: 1000 * 60 * 10, // 10分钟
  });
}

/**
 * 从回收站恢复项目
 */
export function useRestoreProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => projectApi.restore(id),
    onSuccess: () => {
      // 失效项目列表、回收站列表、文件夹树（恢复后项目需重新出现在工作区树中）
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.trash() });
      invalidateFolderTrees(queryClient);
    },
  });
}

/**
 * 永久删除项目
 */
export function usePermanentDeleteProject() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: (id: string) => projectApi.permanentDelete(id),
    onSuccess: (_, id) => {
      // 失效回收站列表
      queryClient.invalidateQueries({ queryKey: projectKeys.trash() });
      // 移除详情缓存
      queryClient.removeQueries({ queryKey: projectKeys.detail(activeOrgId, id) });
    },
  });
}

// ========================================================================
// 缓存操作工具函数（供其他 hooks 调用，避免直接 import projectKeys）
// ========================================================================

/**
 * 失活项目列表缓存。
 * 在创建/删除/移动项目，或项目关联数据（分享、剧本名）变更后调用。
 */
export function invalidateProjectLists(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
}

export function invalidateProjectSubprojects(
  queryClient: QueryClient,
  parentProjectId: string
): void {
  void queryClient.invalidateQueries({ queryKey: projectKeys.subprojectsRoot(parentProjectId) });
}

/**
 * 更新项目列表缓存中的数据（用于乐观更新，如重命名剧本时同步更新项目名）。
 */
export function setProjectListsData(
  queryClient: QueryClient,
  updater: (old: unknown) => unknown
): void {
  queryClient.setQueriesData({ queryKey: projectKeys.lists() }, updater);
}

/**
 * 更新单个项目详情缓存（用于乐观更新）。
 */
export function setProjectDetailData(
  queryClient: QueryClient,
  activeOrgId: string | null,
  projectId: string,
  updater: (oldData: unknown) => unknown
): void {
  queryClient.setQueryData(projectKeys.detail(activeOrgId, projectId), updater);
}

/**
 * 失活单个项目详情缓存。
 * 在更新项目名称等信息后调用，确保详情页显示最新数据。
 */
export function invalidateProjectDetail(
  queryClient: QueryClient,
  activeOrgId: string | null,
  projectId: string
): void {
  void queryClient.invalidateQueries({ queryKey: projectKeys.detail(activeOrgId, projectId) });
}

export function useScreenplayCreateGrants(parentProjectId: string | null, enabled = true) {
  return useQuery({
    queryKey: projectKeys.screenplayCreateGrants(parentProjectId),
    queryFn: () => projectApi.listScreenplayCreateGrants(parentProjectId!),
    enabled: enabled && Boolean(parentProjectId),
  });
}

export function useUpsertScreenplayCreateGrant() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({
      parentProjectId,
      data,
    }: {
      parentProjectId: string;
      data: { userId?: string; orgId?: string; joinEpisodeId?: string };
    }) => projectApi.upsertScreenplayCreateGrant(parentProjectId, data),
    onSuccess: async (_summary, { parentProjectId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: projectKeys.screenplayCreateGrants(parentProjectId),
        }),
        queryClient.invalidateQueries({
          queryKey: projectKeys.memberManagement(activeOrgId ?? null, parentProjectId),
        }),
      ]);
    },
  });
}

export function useRevokeScreenplayCreateGrant() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({
      parentProjectId,
      data,
    }: {
      parentProjectId: string;
      data: { userId?: string; orgId?: string };
    }) => projectApi.revokeScreenplayCreateGrant(parentProjectId, data),
    onSuccess: async (_summary, { parentProjectId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: projectKeys.screenplayCreateGrants(parentProjectId),
        }),
        queryClient.invalidateQueries({
          queryKey: projectKeys.memberManagement(activeOrgId ?? null, parentProjectId),
        }),
      ]);
    },
  });
}
